import React, {useState} from 'react';
import Button from '../../libs/Button/Button';
import notify from '../../libs/Notify/Notify';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';

// Self-service page for someone who only has a client_id/client_secret (not
// an admin account) - they log in with those right here. The admin-picked
// modules/quizzes/courses on client creation are only a starting set: from
// here the client can add any of them to its own access (see
// /api/public/scope). Editing an entity's content reuses the existing admin
// CourseModule/Quiz/Course pages (chips link there) rather than a second,
// more limited edit form here - clients can edit every field there, not just
// name, same as staff can. Deliberately NOT using libs/http/http - that
// always sends the logged-in admin's own token, and this page authenticates
// as the API client instead, so it talks to the api directly with fetch().
const API_BASE = window.env.domain + '/api';

// One entry per scopable entity type.
const ENTITY_KINDS = [
    {field: 'allowedModules', label: 'Модули', scopedPath: '/public/course-modules', catalogPath: '/public/catalog/course-modules', linkPrefix: '/course-module/'},
    {field: 'allowedQuizzes', label: 'Квизы', scopedPath: '/public/quizzes', catalogPath: '/public/catalog/quizzes', linkPrefix: '/quizes/'},
    {field: 'allowedCourses', label: 'Курсы', scopedPath: '/public/courses', catalogPath: '/public/catalog/courses', linkPrefix: '/course/'},
];

function apiFetch(path, token, opts) {
    opts = opts || {};
    return fetch(API_BASE + path, {
        method: opts.method || 'GET',
        headers: Object.assign({'Content-Type': 'application/json'}, token ? {Authorization: token} : {}),
        body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(res => res.json().then(data => {
        if (!res.ok) {
            throw new Error(data.error || 'Request failed');
        }
        return data;
    }))
}

function ClientPortal() {
    let [tokens, setTokens] = useState(null);
    let [clientId, setClientId] = useState('');
    let [clientSecret, setClientSecret] = useState('');
    let [loading, setLoading] = useState(false);

    function onLogin(e) {
        e.preventDefault();
        setLoading(true);
        apiFetch('/auth/client-token', null, {method: 'POST', body: {client_id: clientId, client_secret: clientSecret}})
            .then(r => {
                setLoading(false);
                setTokens(r);
            })
            .catch(e => {
                setLoading(false);
                notify.error('Неверный client_id / client_secret');
            })
    }

    if (!tokens) {
        return (<div style={{maxWidth: 400}}>
            <h3>Доступ по API-ключу</h3>
            <form onSubmit={onLogin}>
                <input type="text" placeholder="client_id" className="form-control mb-10"
                       value={clientId} onChange={e => setClientId(e.target.value)}/>
                <input type="text" placeholder="client_secret" className="form-control mb-10"
                       value={clientSecret} onChange={e => setClientSecret(e.target.value)}/>
                <Button type="submit" disabled={loading}>Войти</Button>
            </form>
        </div>)
    }

    return <ClientEntities accessToken={tokens.access_token} onLogout={() => setTokens(null)}/>
}

function ClientEntities(props) {
    let {accessToken, onLogout} = props;
    let [loaded, setLoaded] = useState(false);
    let [catalogs, setCatalogs] = useState({}); // field -> full catalog items[]
    let [ids, setIds] = useState({}); // field -> selected ids[]

    if (!loaded) {
        setLoaded(true);
        Promise.all(ENTITY_KINDS.map(kind => Promise.all([
            apiFetch(kind.scopedPath, accessToken),
            apiFetch(kind.catalogPath, accessToken),
        ]))).then(results => {
            let newIds = {}, newCatalogs = {};
            ENTITY_KINDS.forEach((kind, i) => {
                let [mine, all] = results[i];
                newIds[kind.field] = (mine.items || []).map(it => it._id);
                newCatalogs[kind.field] = all.items || [];
            });
            setIds(newIds);
            setCatalogs(newCatalogs);
        }).catch(() => notify.error('Не удалось загрузить данные'))
    }

    function onSaveScope() {
        let body = {};
        ENTITY_KINDS.forEach(kind => body[kind.field] = ids[kind.field] || []);
        apiFetch('/public/scope', accessToken, {method: 'PUT', body})
            .then(() => notify.success('Доступ обновлён'))
            .catch(() => notify.error('Не удалось сохранить'))
    }

    return (<div>
        <Button onClick={cb => {
            onLogout();
            cb()
        }}>Выйти</Button>

        <h4 className="mt-10">Мой доступ</h4>
        {ENTITY_KINDS.map(kind => (
            <EntityPicker
                key={kind.field}
                label={kind.label}
                placeholder={`Добавить: ${kind.label}`}
                options={catalogs[kind.field] || []}
                selectedIds={ids[kind.field] || []}
                onChange={selected => setIds(v => ({...v, [kind.field]: selected}))}
                linkPrefix={kind.linkPrefix}
            />
        ))}
        <Button className="mt-10" onClick={cb => {
            onSaveScope();
            cb()
        }}>Сохранить доступ</Button>
    </div>)
}

// Chips are clickable (navigates to the entity's normal admin edit page,
// where every field can be changed) as well as removable (the delete icon
// still just drops it from this client's access, via onChange).
function EntityPicker(props) {
    let {label, placeholder, options, selectedIds, onChange, linkPrefix} = props;
    return (<div className="mt-10" style={{maxWidth: 500}}>
        <Autocomplete
            multiple
            options={options}
            getOptionLabel={option => `${option.name} (#${option._id})`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={options.filter(option => selectedIds.includes(option._id))}
            filterSelectedOptions
            onChange={(e, values) => onChange(values.map(v => v._id))}
            renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip
                    {...getTagProps({index})}
                    key={option._id}
                    label={`${option.name} (#${option._id})`}
                    onClick={() => global.navigate(linkPrefix + option._id)}
                />
            ))}
            renderInput={params => <TextField {...params} label={label} placeholder={placeholder}/>}
        />
    </div>)
}

export default ClientPortal
